import { Transaction } from '../types';
import { format, parseISO, subDays, startOfMonth, endOfMonth, eachDayOfInterval, eachMonthOfInterval, subMonths } from 'date-fns';

// Color palette for categories
const CATEGORY_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
  '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D2B4DE'
];

// Interface for category data
interface CategoryData {
  name: string;
  amount: number;
  percentage: number;
  color: string;
  transactionCount: number;
}

// Interface for monthly data
interface MonthlyData {
  month: string;
  income: number;
  expenses: number;
  savings: number;
  net: number;
}

// Interface for daily spending data
interface DailySpendingData {
  label: string;
  value: number;
  date: Date;
}

// Interface for spending insights
interface SpendingInsights {
  totalSpending: number;
  avgDailySpending: number;
  currentMonthExpenses: number;
  lastMonthExpenses: number;
  topCategories: Array<{
    name: string;
    amount: number;
    percentage: number;
    color: string;
  }>;
  spendingTrend: 'increasing' | 'decreasing' | 'stable';
  trendDirection: 'increasing' | 'decreasing' | 'stable';
  monthlyChange: number;
}

// Format currency values
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(amount));
};

// Format percentage values
export const formatPercentage = (percentage: number): string => {
  return `${percentage.toFixed(1)}%`;
};

// Get color for trend indicators
export const getTrendColor = (trend: 'increasing' | 'decreasing' | 'stable', theme: any): string => {
  switch (trend) {
    case 'increasing':
      return theme.palette.success.main;
    case 'decreasing':
      return theme.palette.error.main;
    case 'stable':
    default:
      return theme.palette.info.main;
  }
};

// Process transactions for category breakdown
export const processCategoryData = (
  transactions: Transaction[],
  type: 'expense' | 'income' | 'savings' = 'expense'
): CategoryData[] => {
  // Filter transactions by type
  const filteredTransactions = transactions.filter(t => {
    if (type === 'savings') {
      // For savings, we might want to show categories that help with savings
      // For now, let's treat savings as a special case of expenses
      return t.type === 'expense' && t.category.toLowerCase().includes('savings');
    }
    return t.type === type;
  });

  // Group by category
  const categoryMap = new Map<string, { amount: number; count: number }>();
  
  filteredTransactions.forEach(transaction => {
    const category = transaction.category || 'Other';
    const existing = categoryMap.get(category) || { amount: 0, count: 0 };
    categoryMap.set(category, {
      amount: existing.amount + transaction.amount,
      count: existing.count + 1
    });
  });

  // Calculate total amount
  const totalAmount = Array.from(categoryMap.values()).reduce((sum, cat) => sum + cat.amount, 0);

  // Convert to array with additional data
  const categoryData: CategoryData[] = Array.from(categoryMap.entries())
    .map(([name, data], index) => ({
      name,
      amount: data.amount,
      percentage: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0,
      color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
      transactionCount: data.count
    }))
    .sort((a, b) => b.amount - a.amount);

  return categoryData;
};

// Process transactions for monthly analysis
export const processMonthlyData = (
  transactions: Transaction[],
  months: number = 12
): MonthlyData[] => {
  const endDate = new Date();
  const startDate = subMonths(startOfMonth(endDate), months - 1);
  
  const monthlyData: MonthlyData[] = [];
  const monthIntervals = eachMonthOfInterval({ start: startDate, end: endDate });

  monthIntervals.forEach(monthStart => {
    const monthEnd = endOfMonth(monthStart);
    const monthTransactions = transactions.filter(t => {
      const transactionDate = parseISO(t.date);
      return transactionDate >= monthStart && transactionDate <= monthEnd;
    });

    const income = monthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const expenses = monthTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    // For now, let's calculate savings as a percentage of income
    // or look for specific savings categories
    const savings = monthTransactions
      .filter(t => t.type === 'expense' && t.category.toLowerCase().includes('savings'))
      .reduce((sum, t) => sum + t.amount, 0);

    monthlyData.push({
      month: format(monthStart, 'MMM yyyy'),
      income,
      expenses,
      savings,
      net: income - expenses - savings
    });
  });

  return monthlyData;
};

// Process transactions for daily spending analysis
export const processDailySpending = (
  transactions: Transaction[],
  days: number = 30
): DailySpendingData[] => {
  const endDate = new Date();
  const startDate = subDays(endDate, days - 1);
  
  const dailyData: DailySpendingData[] = [];
  const dayIntervals = eachDayOfInterval({ start: startDate, end: endDate });

  dayIntervals.forEach(day => {
    const dayTransactions = transactions.filter(t => {
      const transactionDate = parseISO(t.date);
      return format(transactionDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
    });

    const dailySpending = dayTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    dailyData.push({
      label: format(day, 'MMM dd'),
      value: dailySpending,
      date: day
    });
  });

  return dailyData;
};

// Calculate spending insights
export const calculateSpendingInsights = (
  transactions: Transaction[],
  days: number = 30
): SpendingInsights => {
  const expenseTransactions = transactions.filter(t => t.type === 'expense');
  const totalSpending = expenseTransactions.reduce((sum, t) => sum + t.amount, 0);
  const avgDailySpending = totalSpending / days;

  // Calculate current month and last month expenses
  const currentMonth = new Date();
  const lastMonth = subMonths(currentMonth, 1);
  
  const currentMonthExpenses = expenseTransactions.filter(t => {
    const transactionDate = parseISO(t.date);
    return transactionDate >= startOfMonth(currentMonth) && transactionDate <= endOfMonth(currentMonth);
  }).reduce((sum, t) => sum + t.amount, 0);

  const lastMonthExpenses = expenseTransactions.filter(t => {
    const transactionDate = parseISO(t.date);
    return transactionDate >= startOfMonth(lastMonth) && transactionDate <= endOfMonth(lastMonth);
  }).reduce((sum, t) => sum + t.amount, 0);

  // Get top categories
  const categoryData = processCategoryData(transactions, 'expense');
  const topCategories = categoryData.slice(0, 5).map(cat => ({
    name: cat.name,
    amount: cat.amount,
    percentage: cat.percentage,
    color: cat.color
  }));

  // Calculate spending trend (simplified)
  const recentTransactions = transactions.filter(t => {
    const transactionDate = parseISO(t.date);
    const cutoffDate = subDays(new Date(), 15);
    return transactionDate >= cutoffDate && t.type === 'expense';
  });

  const olderTransactions = transactions.filter(t => {
    const transactionDate = parseISO(t.date);
    const endDate = subDays(new Date(), 15);
    const startDate = subDays(endDate, 15);
    return transactionDate >= startDate && transactionDate < endDate && t.type === 'expense';
  });

  const recentSpending = recentTransactions.reduce((sum, t) => sum + t.amount, 0);
  const olderSpending = olderTransactions.reduce((sum, t) => sum + t.amount, 0);

  const monthlyChange = olderSpending > 0 ? ((recentSpending - olderSpending) / olderSpending) * 100 : 0;
  const spendingTrend = monthlyChange > 10 ? 'increasing' : monthlyChange < -10 ? 'decreasing' : 'stable';

  return {
    totalSpending,
    avgDailySpending,
    currentMonthExpenses,
    lastMonthExpenses,
    topCategories,
    spendingTrend,
    trendDirection: spendingTrend, // Same as spendingTrend for now
    monthlyChange
  };
};

// Interface for Sankey flow data
interface SankeyNode {
  id: string;
  label?: string;
  color?: string;
}

interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

// Process transactions for Sankey flow diagram
export const processSankeyData = (transactions: Transaction[]): SankeyData => {
  const nodes: SankeyNode[] = [];
  const links: SankeyLink[] = [];
  
  // Calculate totals for each income category
  const incomeCategories = new Map<string, number>();
  const expenseCategories = new Map<string, number>();
  
  transactions.forEach(transaction => {
    const category = transaction.category || 'Other';
    
    if (transaction.type === 'income') {
      incomeCategories.set(category, (incomeCategories.get(category) || 0) + transaction.amount);
    } else if (transaction.type === 'expense') {
      expenseCategories.set(category, (expenseCategories.get(category) || 0) + transaction.amount);
    }
  });
  
  // Create income nodes and links to "Available Money"
  let colorIndex = 0;
  incomeCategories.forEach((amount, category) => {
    const nodeId = `income-${category}`;
    nodes.push({
      id: nodeId,
      label: category,
      color: CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length]
    });
    
    links.push({
      source: nodeId,
      target: 'available-money',
      value: amount
    });
    colorIndex++;
  });
  
  // Add central "Available Money" node
  nodes.push({
    id: 'available-money',
    label: 'Available Money',
    color: '#95A5A6'
  });
  
  // Create expense nodes and links from "Available Money"
  expenseCategories.forEach((amount, category) => {
    const nodeId = `expense-${category}`;
    nodes.push({
      id: nodeId,
      label: category,
      color: CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length]
    });
    
    links.push({
      source: 'available-money',
      target: nodeId,
      value: amount
    });
    colorIndex++;
  });
  
  // Calculate total income and expenses for savings
  const totalIncome = Array.from(incomeCategories.values()).reduce((sum, amount) => sum + amount, 0);
  const totalExpenses = Array.from(expenseCategories.values()).reduce((sum, amount) => sum + amount, 0);
  const savings = totalIncome - totalExpenses;
  
  // Add savings node if there are savings
  if (savings > 0) {
    nodes.push({
      id: 'savings',
      label: 'Savings',
      color: '#27AE60'
    });
    
    links.push({
      source: 'available-money',
      target: 'savings',
      value: savings
    });
  }
  
  return { nodes, links };
};

// Export interfaces for use in components
export type { CategoryData, MonthlyData, DailySpendingData, SpendingInsights, SankeyNode, SankeyLink, SankeyData }; 